import React, { createContext, useState, useContext, useEffect } from 'react';
import tasksData from '../tasks.json';

const GameContext = createContext();

export const useGameContext = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
  // Game configuration
  const [startingLevel, setStartingLevel] = useState(1);
  const [roundsPerLevel, setRoundsPerLevel] = useState(4);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [playerNames, setPlayerNames] = useState({ player1: 'André', player2: 'Sumi' });
  const [currentPlayer, setCurrentPlayer] = useState('player1');
  const [roundsPlayed, setRoundsPlayed] = useState({ player1: 0, player2: 0 });
  const [rejectedKinks, setRejectedKinks] = useState(["bdsm", "anal", "porn", "cheating", "lipstick", "feet", "roleplay", "music", "alcohol", "public"]);

  // Task selection and display
  const [selectedTask, setSelectedTask] = useState(null);
  const [availableKinks, setAvailableKinks] = useState([]);
  const [selectedKink, setSelectedKink] = useState(null);
  const [cardOptions, setCardOptions] = useState([]);
  const [showTaskText, setShowTaskText] = useState(false);

  // Player details - assuming player1 is male and player2 is female
  const playerDetails = {
    player1: {
      name: playerNames.player1,
      bodyType: 'has_penis',
      pronouns: {
        subject: 'he',
        object: 'him',
        possessive: 'his',
        genitals: 'penis'
      }
    },
    player2: {
      name: playerNames.player2,
      bodyType: 'has_vagina',
      pronouns: {
        subject: 'she',
        object: 'her',
        possessive: 'her',
        genitals: 'vulva'
      }
    }
  };

  // Initialize the game
  useEffect(() => {
    if (startingLevel > 1) {
      setCurrentLevel(startingLevel);
    }
  }, [startingLevel]);

  // Extract all unique kinks for the current level
  useEffect(() => {
    const eligibleTasks = getEligibleTasks();
    const kinks = new Set();

    eligibleTasks.forEach(task => {
      if (task.kinks.length === 0) {
        kinks.add('Other');
      } else {
        task.kinks.forEach(kink => kinks.add(kink));
      }
    });

    setAvailableKinks([...kinks].sort());
  }, [currentLevel, currentPlayer]);

  // Generate card options whenever the current player changes
  useEffect(() => {
    generateCardOptions();
  }, [currentPlayer, currentLevel]);

  // Function to get tasks eligible for the current player and level
  const getEligibleTasks = () => {
    const performer = playerDetails[currentPlayer];
    const receiver = playerDetails[currentPlayer === 'player1' ? 'player2' : 'player1'];

    return tasksData.filter(task => {
      // First check if the task level matches the current level
      const levelMatches = task.level === currentLevel;

      // Check if performer body type requirements are met
      const performerMatches = task.performer_body_type === 'any' ||
                              task.performer_body_type === performer.bodyType;

      // Check if receiver body type requirements are met
      const receiverMatches = task.receiver_body_type === 'any' ||
                             task.receiver_body_type === receiver.bodyType;

      const hasRejectedKinks = task.kinks.some(kink => rejectedKinks.includes(kink));

      // Return true only if all conditions are satisfied
      return levelMatches && performerMatches && receiverMatches && !hasRejectedKinks;
    });
  };

  // Generate card options for Cards game mode
  const generateCardOptions = () => {
    const eligibleTasks = getEligibleTasks();
    const options = [];
    const selectedIndices = new Set();

    // Try to get 4 unique tasks
    while (options.length < 4 && options.length < eligibleTasks.length) {
      const randomIndex = Math.floor(Math.random() * eligibleTasks.length);
      if (!selectedIndices.has(randomIndex)) {
        selectedIndices.add(randomIndex);
        options.push(eligibleTasks[randomIndex]);
      }
    }

    setCardOptions(options);
  };

  // Select a random task from the selected kink
  const selectRandomTaskByKink = (kink) => {
    const eligibleTasks = getEligibleTasks();
    let kinkTasks;

    if (kink === 'Other') {
      kinkTasks = eligibleTasks.filter(task => task.kinks.length === 0);
    } else {
      kinkTasks = eligibleTasks.filter(task => task.kinks.includes(kink));
    }

    if (kinkTasks.length > 0) {
      const randomIndex = Math.floor(Math.random() * kinkTasks.length);
      const task = kinkTasks[randomIndex];

      setSelectedTask(task);
      setShowTaskText(true);
      return task;
    } else {
      // If somehow no tasks found, try another kink
      const randomKink = availableKinks[Math.floor(Math.random() * availableKinks.length)];
      return selectRandomTaskByKink(randomKink);
    }
  };

  // Process template text, replacing placeholders with actual names and pronouns
  const processTemplateText = (template, hasTimer, timerDuration) => {
    if (!template) return '';

    const performer = playerDetails[currentPlayer];
    const receiver = playerDetails[currentPlayer === 'player1' ? 'player2' : 'player1'];

    let processedText = template
      .replace(/:performer/g, performer.name)
      .replace(/:receiver/g, receiver.name)
      .replace(/:p_pos/g, performer.pronouns.possessive)
      .replace(/:p_obj/g, performer.pronouns.object)
      .replace(/:p_subj/g, performer.pronouns.subject)
      .replace(/:p_genitals/g, performer.pronouns.genitals)
      .replace(/:r_pos/g, receiver.pronouns.possessive)
      .replace(/:r_obj/g, receiver.pronouns.object)
      .replace(/:r_subj/g, receiver.pronouns.subject)
      .replace(/:r_genitals/g, receiver.pronouns.genitals);

    // Replace timer placeholder with the actual time
    if (hasTimer) {
      processedText = processedText.replace(/:timer/g, `${timerDuration} seconds`);
    }

    return processedText;
  };

  const skipTask = () => {
    setSelectedTask(null);
    setShowTaskText(false);
  }

  // Handle task completion
  const completeTask = () => {
    // Update rounds played for the current player
    const updatedRoundsPlayed = {
      ...roundsPlayed,
      [currentPlayer]: roundsPlayed[currentPlayer] + 1
    };
    setRoundsPlayed(updatedRoundsPlayed);

    // Check if both players have completed their rounds for the current level
    const player1Completed = currentPlayer === 'player1' ?
      updatedRoundsPlayed.player1 >= roundsPerLevel :
      roundsPlayed.player1 >= roundsPerLevel;

    const player2Completed = currentPlayer === 'player2' ?
      updatedRoundsPlayed.player2 >= roundsPerLevel :
      roundsPlayed.player2 >= roundsPerLevel;

    if (player1Completed && player2Completed) {
      // Both players completed their rounds, move to next level
      setCurrentLevel(currentLevel + 1);
      setRoundsPlayed({ player1: 0, player2: 0 });
      alert(`Level ${currentLevel} completed! Moving to level ${currentLevel + 1}`);
    } else {
      // Switch players
      setCurrentPlayer(currentPlayer === 'player1' ? 'player2' : 'player1');
      // Card options will be generated in the useEffect when currentPlayer changes
    }

    // Reset task selection
    setSelectedTask(null);
    setShowTaskText(false);
  };

  return (
    <GameContext.Provider
      value={{
        startingLevel,
        setStartingLevel,
        roundsPerLevel,
        setRoundsPerLevel,
        currentLevel,
        playerNames,
        setPlayerNames,
        currentPlayer,
        roundsPlayed,
        selectedTask,
        setSelectedTask,
        availableKinks,
        selectedKink,
        setSelectedKink,
        cardOptions,
        showTaskText,
        setShowTaskText,
        playerDetails,
        getEligibleTasks,
        generateCardOptions,
        selectRandomTaskByKink,
        processTemplateText,
        completeTask,
        skipTask,
        rejectedKinks,
        setRejectedKinks
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
